import { useContext, useCallback, useState, useEffect } from "react";
import { FireBaseContext } from "../../Context/FireBase";
import {
  getDocs,
  where,
  query,
  onSnapshot,
  getDoc,
  doc,
} from "firebase/firestore";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { HelperContext } from "../../Context/HelperContext";

function ExportNewSfda({
  data,
  filename,
  sheetname,
  onExportStart,
  onExportEnd,
}) {
  const { SubscribersRefrence, TransferOfValuesRef , database } =
    useContext(FireBaseContext);

  const { FormateDate } = useContext(HelperContext);

  const [TOVs, setTOVs] = useState([]);

  useEffect(() => {
    const getTovData = async () => {
      try {
        onSnapshot(TransferOfValuesRef, (snapshot) => {
          const newData = snapshot.docs.map((doc) => doc.id);
          setTOVs(newData);
        });
      } catch (error) {
        console.error("Error fetching data: ", error);
      }
    };

    getTovData();
  }, []);

  const getUserNameById = async (id) => {
    const userRef = doc(database, "Users", id);
    const userSnapshot = await getDoc(userRef);
    if (userSnapshot.exists()) {
      const userData = userSnapshot.data();
      return userData.Name;
    }
  };

  const handleExport = useCallback(async () => {
    if (TOVs.length > 0) {
      onExportStart();

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet(sheetname);
      const finalDataToExport = [];

      await Promise.all(
        data?.map(async (item) => {
          const subscribersQuery = query(
            SubscribersRefrence,
            where("eventID", "==", item.ID.toString())
          );
          const createdByName = await getUserNameById(item.CreatedByID);

          await getDocs(subscribersQuery).then((snapshot) => {
            finalDataToExport.push(
              {
                "Event ID": item.Id,
                "Event Name": item.eventName,
                "Franchise Name": item.Franchise?.toString(),
                "Event City": item.city.map((city) => city).join(","),
                "Cost per Delegate": item.CostperDelegate,
                "Event Cost": item.eventCost?.toString(),
                PO: item.PO,
                BeSure: item.BeSure,
                "Event Date": FormateDate(item.eventDate),
                "Created At": item.CreatedAt,
                "Created By": createdByName,
              },
              ...snapshot.docs.map((ele) => {
                const subItem = ele.data();
                const totalGrant = subItem.TransferOfValue.reduce(
                  (first, second) => {
                    return Number(first) + Number(second.value);
                  },
                  0
                );

                let returnedTovs = [];

                TOVs.forEach((tov) => {
                  const total = subItem.TransferOfValue.filter(
                    (transfer) => transfer.types === tov
                  ).reduce(
                    (total, transfer) => total + Number(transfer.value),
                    0
                  );

                  returnedTovs.push({
                    type: tov,
                    value: total,
                  });
                });

                let values = {
                  "Title/اللقب": "Dr",
                  "FirstName/الاسم الاول": subItem.name,
                  "LastName/الاسم الاخير": subItem.LastName,
                  Specialitzation: subItem.specialty,
                  "National/Resident ID": subItem.nationalId,
                  "Medical ID": subItem.licenceId || "",
                  "Payment Method": "cash or cash equivalent",
                  "Payment Method Other": "",
                  "Grant purpose": subItem.TransferOfValue.map(
                    (item) => `${item.types} = ${item.value}`
                  ).join(","),
                  "Payment Amount": totalGrant,
                  "Date of Payment": item.eventDate,
                  Organization: subItem.organization,
                  Signature: subItem.signURL
                    ? { text: "Signature Link", hyperlink: subItem.signURL }
                    : "",
                };

                returnedTovs.forEach((tov) => {
                  values[tov.type] = tov.value;
                });

                return values;
              })
            );
          });
        })
      );

      // Add headers
      const headersList = [
        "Event Name",
        "Franchise Name",
        "Event City",
        "Cost per Delegate",
        "Event Cost",
        "PO",
        "BeSure",
        "Event Date",
        "Created At",
        "Created By",
        "Event ID",
        "Title/اللقب",
        "FirstName/الاسم الاول",
        "LastName/الاسم الاخير",
        "Specialitzation",
        "National/Resident ID",
        "Medical ID",
        "Payment Method",
        "Payment Method Other",
        "Grant purpose",
        ...TOVs,
        "Payment Amount",
        "Date of Payment",
        "Organization",
        "Signature",
      ];

      worksheet.addRow([...headersList]);

      // Set the background color for the entire row
      const rowIndex = 1;
      const row = worksheet.getRow(rowIndex);
      row.eachCell({ includeEmpty: true }, (cell, index) => {
        const color =
          index > 11 ? "80FFFF00" : index === 11 ? "80009900" : "ff00b0f0";
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: color },
        };
        cell.font = { size: 12 };
        cell.alignment = { horizontal: "center", vertical: "middle" };
      });

      // Add data rows
      let eventRow = {};
      finalDataToExport.forEach((rowItem, index) => {
        if (rowItem.hasOwnProperty("Event Name")) {
          eventRow = { ...rowItem };
        } else {
          rowItem = {
            ...eventRow,
            ...rowItem,
          };
        }
        const holder = headersList.map((head) => {
          const convertItem = isNaN(Number(rowItem[head]))
            ? rowItem[head]
            : Number(rowItem[head]);
          return rowItem[head] ? convertItem : "";
        });

        worksheet.addRow([...holder]);

        const currentRow = worksheet.getRow(index + 2);
        currentRow.eachCell({ includeEmpty: true }, (cell) => {
          if (
            rowItem.hasOwnProperty("Event Name") &&
            !rowItem.hasOwnProperty("Title/اللقب")
          ) {
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: "FFADD8E6" }, // light blue
            };
          }
          cell.alignment = { horizontal: "center", vertical: "middle" };
          if (typeof cell.value === "number") {
            cell.numFmt = "0";
          }
        });
      });

      // Set column widths
      worksheet.columns.forEach((column, colIndex) => {
        let maxLength = 0;
        worksheet.eachRow({ includeEmpty: true }, (row) => {
          const cellValue = row.getCell(colIndex + 1).text;
          maxLength = Math.max(maxLength, cellValue ? cellValue.length : 0);
        });
        column.width = maxLength + 5;
      });

      worksheet.getRow(1).height = 20;

      // Save the workbook
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      saveAs(blob, filename);

      onExportEnd();
    }
  }, [
    TOVs,
    data,
    filename,
    sheetname,
    SubscribersRefrence,
    onExportStart,
    onExportEnd,
  ]);
  return (
    <i className="fa-solid fa-download" onClick={handleExport}>
      <span className="fs-6 fw-light"> SFDA 2025</span>
    </i>
  );
}

export default ExportNewSfda;
